import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { AlertCircle, Check, CheckCircle2, Loader2, Lock } from "lucide-react";
import { ImageWithFallback } from "../../landing/ImageWithFallback";

type Props = {
  start: string;
  end: string;
  disabled?: boolean;
  locked?: boolean;
  onCommit: (next: {
    start: string;
    end: string;
  }) => Promise<number | undefined>;
  className?: string;
};

function onlyDigits(v: string) {
  return (v ?? "").replace(/\D/g, "");
}
function only2Digits(v: string) {
  return onlyDigits(v).slice(0, 2);
}
function pad2Loose(v: string) {
  // blur: "" => "", "9" => "09", "15" => "15"
  const d = onlyDigits(v);
  if (d.length === 0) return "";
  if (d.length === 1) return `0${d}`;
  return d.slice(0, 2);
}
function parseHHMM(t: string) {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(t);
  if (!m) return null;
  return { hh: m[1], mm: m[2] };
}
function toMin(hh: string, mm: string) {
  return Number(hh) * 60 + Number(mm);
}
function isHH(v: string) {
  if (v.length !== 2) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 23;
}
function isMM(v: string) {
  if (v.length !== 2) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 59;
}

export function TimeRangeInput({
  start,
  end,
  disabled,
  onCommit,
  className,
  locked
}: Props) {
  const defaultStart = parseHHMM(start) ?? { hh: "09", mm: "00" };
  const defaultEnd = parseHHMM(end) ?? { hh: "11", mm: "00" };
  type SaveState = "idle" | "saving" | "saved" | "error";

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const lastSavedRef = useRef<string>(`${start}-${end}`);
  const retryRef = useRef<null | (() => void)>(null);
  const savingRef = useRef(false);

  const lastGoodRef = useRef<{ start: string; end: string }>({ start, end });
  const savedTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);

  const [h1, setH1] = useState(defaultStart.hh);
  const [m1, setM1] = useState(defaultStart.mm);
  const [h2, setH2] = useState(defaultEnd.hh);
  const [m2, setM2] = useState(defaultEnd.mm);

  // Refs
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rH1 = useRef<HTMLInputElement | null>(null);
  const rM1 = useRef<HTMLInputElement | null>(null);
  const rH2 = useRef<HTMLInputElement | null>(null);
  const rM2 = useRef<HTMLInputElement | null>(null);

  // Sync from props when props change (only when not focused inside)
  useEffect(() => {
    const s2 = parseHHMM(start) ?? { hh: "09", mm: "00" };
    const e2 = parseHHMM(end) ?? { hh: "11", mm: "00" };
    setH1(s2.hh);
    setM1(s2.mm);
    setH2(e2.hh);
    setM2(e2.mm);
  }, [start, end]);

  async function saveTimeRange(nextStart: string, nextEnd: string) {
    const key = `${nextStart}-${nextEnd}`;

    if (key === lastSavedRef.current) return;

    if (savingRef.current) {
      return;
    }

    savingRef.current = true;

    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);

    setSaveState("saving");

    try {
      const result = await onCommit({ start: nextStart, end: nextEnd });

      lastSavedRef.current = key;

      if (result && result > 0) {
        lastSavedRef.current = key;
        lastGoodRef.current = { start: nextStart, end: nextEnd };
        setSaveState("saved");
      } else {
        setSaveState("error");
        restoreToLastGood();
      }
    } catch (e) {
      setSaveState("error");
      restoreToLastGood();
      retryRef.current = () => saveTimeRange(nextStart, nextEnd);
    } finally {
      savingRef.current = false;
      idleTimerRef.current = window.setTimeout(
        () => setSaveState("idle"),
        2000,
      );
    }
  }

  function restoreToLastGood() {
    const s2 = parseHHMM(lastGoodRef.current.start) ?? { hh: "09", mm: "00" };
    const e2 = parseHHMM(lastGoodRef.current.end) ?? { hh: "11", mm: "00" };
    setH1(s2.hh);
    setM1(s2.mm);
    setH2(e2.hh);
    setM2(e2.mm);
  }

  function onWrapBlurCapture() {
    const nh1 = normalize2(h1);
    const nm1 = normalize2(m1);
    const nh2 = normalize2(h2);
    const nm2 = normalize2(m2);
    const ok = /* validate HH/MM + start<end */ true;
    if (!ok) return;

    setH1(nh1);
    setM1(nm1);
    setH2(nh2);
    setM2(nm2);

    saveTimeRange(`${nh1}:${nm1}`, `${nh2}:${nm2}`);
  }

  const normalized = useMemo(() => {
    // For validation/commit we require 2 digits each
    return { hh1: h1, mm1: m1, hh2: h2, mm2: m2 };
  }, [h1, m1, h2, m2]);

  const normalize2 = (v: string) => {
    const d = v.replace(/\D/g, "");
    if (!d) return "";
    if (d.length === 1) return `0${d}`;
    return d.slice(0, 2);
  };

  const validFields =
    isHH(normalized.hh1) &&
    isMM(normalized.mm1) &&
    isHH(normalized.hh2) &&
    isMM(normalized.mm2);

  const validRange =
    validFields &&
    toMin(normalized.hh1, normalized.mm1) <
      toMin(normalized.hh2, normalized.mm2);

  function selectAll(el: HTMLInputElement | null) {
    if (!el) return;
    el.focus();
    el.setSelectionRange(0, el.value.length);
  }

  // Click vào vùng cha nhưng không click đúng input => focus & select HH1
  function onWrapperMouseDown(e: React.MouseEvent) {
    if (disabled) return;

    const target = e.target as HTMLElement;
    const clickedInput = target.tagName === "INPUT";

    if (!clickedInput) {
      e.preventDefault(); // tránh mất focus do browser default
      requestAnimationFrame(() => selectAll(rH1.current));
    }
  }

  // Blur: pad "9" -> "09"
  function blurPadHH(setter: (v: string) => void, v: string) {
    const padded = pad2Loose(v);
    // nếu padded có 2 digit => giữ; nếu "" => giữ rỗng
    setter(padded);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // if (e.key === "Enter") {
    //   e.preventDefault();
    //   commit();
    // }
    if (e.key === "Escape") {
      e.preventDefault();
      const s2 = parseHHMM(start) ?? { hh: "09", mm: "00" };
      const e2 = parseHHMM(end) ?? { hh: "11", mm: "00" };
      setH1(s2.hh);
      setM1(s2.mm);
      setH2(e2.hh);
      setM2(e2.mm);
      requestAnimationFrame(() => selectAll(rH1.current));
    }
  }

  function SaveIcon({
    state,
    onRetry,
  }: {
    state: SaveState;
    onRetry?: () => void;
  }) {
    if (state === "saving")
      return (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground ml-2" />
      );
    if (state === "saved")
      return <Check className="h-5 w-5 text-green-600 font-bold  ml-2" />;
    if (state === "error")
      return <AlertCircle className="h-5 w-5 text-red-600  ml-2" />;
    return null;
  }

  useEffect(() => {
    lastGoodRef.current = { start, end };
    lastSavedRef.current = `${start}-${end}`;
  }, [start, end]);

  const inputCls =
    "max-w-[22.1px] w-auto input-font bg-transparent text-blue-500 text-base text-center tabular-nums " +
    "outline-none border-0 shadow-none ring-0 focus:ring-0 focus:outline-none focus-visible:outline-none";

  const sepCls = "text-primary text-sm font-bold select-none";

  return (
    <div className={cn("inline-flex flex-col gap-1 cursor-text", className)}>
      <div
        ref={wrapRef}
        onMouseDown={onWrapperMouseDown}
        onBlurCapture={(e) => {
          const next = e.relatedTarget as Node | null;
          const stillInside = !!next && !!wrapRef.current?.contains(next);
          if (stillInside) return;
          onWrapBlurCapture();
        }}
        className={cn(
          "relative inline-flex items-center rounded-md px-2 py-1 border border-transparent",
          "hover:border-green-700",
          disabled && "opacity-60 cursor-not-allowed hover:bg-primary/10",
        )}
        title="Sửa khung giờ khám"
      >
        <input
          size={2}
          ref={rH1}
          value={h1}
          readOnly={disabled}
          onChange={(e) => setH1(only2Digits(e.target.value))}
          onKeyDown={onKeyDown}
          onFocus={() => selectAll(rH1.current)}
          onBlur={() => blurPadHH(setH1, h1)}
          className={inputCls}
          inputMode="numeric"
        />
        <span className={cn("mx-0.5", sepCls)}>:</span>
        <input
          size={2}
          ref={rM1}
          value={m1}
          readOnly={disabled}
          onChange={(e) => setM1(only2Digits(e.target.value))}
          onKeyDown={onKeyDown}
          onFocus={() => selectAll(rM1.current)}
          onBlur={() => blurPadHH(setM1, m1)}
          className={inputCls}
          inputMode="numeric"
        />
        <span className={cn("mx-1", sepCls)}>-</span>
        <input
          size={2}
          ref={rH2}
          value={h2}
          readOnly={disabled}
          onChange={(e) => setH2(only2Digits(e.target.value))}
          onKeyDown={onKeyDown}
          onFocus={() => selectAll(rH2.current)}
          onBlur={() => blurPadHH(setH2, h2)}
          className={inputCls}
          inputMode="numeric"
        />
        <span className={cn("mx-0.5", sepCls)}>:</span>
        <input
          size={2}
          ref={rM2}
          value={m2}
          readOnly={disabled}
          onChange={(e) => setM2(only2Digits(e.target.value))}
          onKeyDown={onKeyDown}
          onFocus={() => selectAll(rM2.current)}
          onBlur={() => blurPadHH(setM2, m2)}
          className={inputCls}
          inputMode="numeric"
        />
        <SaveIcon state={saveState} onRetry={() => retryRef.current?.()} />
        {locked && (
          <div className="absolute top-0 -right-8 pointer-events-none">
            <ImageWithFallback src="/icons/lock.png" className="w-8 h-8"/>
          </div>
        )}
      </div>

      {!validRange && (
        <div className="text-xs text-red-600 font-medium">
          {validFields
            ? "Giờ bắt đầu phải nhỏ hơn giờ kết thúc"
            : "Giờ/phút không hợp lệ"}
        </div>
      )}
    </div>
  );
}
